import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';

import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';
import { WorkContextService } from '../../../service/admin/work-context.service';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { todayDateString } from '../../../utils/clinic-date.util';

type QueueBoardColumn = 'waiting' | 'with_doctor' | 'paused' | 'done';

interface QueueBoardCard {
  appointment_id: number;
  patient_id: number;
  name: string;
  patient_number?: string;
  time: string;
  status: string;
  checked_in_at?: string | null;
  column: QueueBoardColumn;
}

@Component({
  selector: 'app-queue-board',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkContextBarComponent],
  templateUrl: './queue-board.component.html',
  styleUrl: './queue-board.component.scss',
})
export class QueueBoardComponent implements OnInit, OnDestroy {
  readonly columns: { key: QueueBoardColumn; label: string }[] = [
    { key: 'waiting', label: 'Waiting' },
    { key: 'with_doctor', label: 'With doctor' },
    { key: 'paused', label: 'Paused' },
    { key: 'done', label: 'Done' },
  ];

  cards: QueueBoardCard[] = [];
  loading = false;
  lastRefreshedAt: Date | null = null;
  waitingTimeTick = Date.now();

  private refreshSub?: Subscription;
  private waitingSub?: Subscription;
  private contextSub?: Subscription;

  constructor(
    private appointmentService: AppointmentService,
    private clinicService: ClinicService,
    private patientRegistrationService: PatientRegistrationService,
    private workContext: WorkContextService,
  ) {}

  ngOnInit(): void {
    this.loadBoard();
    this.refreshSub = interval(30000).subscribe(() => this.loadBoard());
    this.waitingSub = interval(30000).subscribe(() => {
      this.waitingTimeTick = Date.now();
    });
    this.contextSub = this.workContext.context$.subscribe(() => this.loadBoard());
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.waitingSub?.unsubscribe();
    this.contextSub?.unsubscribe();
  }

  loadBoard(): void {
    const clinicId = this.workContext.getClinicId();
    const doctorId = this.workContext.getDoctorId();
    if (!clinicId || !doctorId) {
      this.cards = [];
      return;
    }

    this.loading = true;
    this.appointmentService.getQueue({
      clinic_id: clinicId,
      doctor_id: doctorId,
      date: todayDateString(),
      slot: this.clinicService.getSlotDurationMinutes(clinicId),
    }).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.cards = [];
          this.loading = false;
          return;
        }

        const appointments = (res.data?.slots || []).flatMap((slot: any) =>
          (slot.appointments || []).map((appt: any) => ({
            appointment_id: appt.id,
            patient_id: appt.patient_id,
            time: appt.start_time,
            status: appt.status,
            checked_in_at: appt.checked_in_at,
          })),
        );

        const patientIds = appointments.map((a: any) => a.patient_id);
        if (!patientIds.length) {
          this.cards = [];
          this.loading = false;
          this.lastRefreshedAt = new Date();
          return;
        }

        this.patientRegistrationService.getPatientsBatch(patientIds).subscribe({
          next: (patientRes: any) => {
            const patientsMap = new Map<number, any>();
            if (patientRes?.success) {
              (patientRes.data || []).forEach((p: any) => patientsMap.set(p.id, p));
            }

            this.cards = appointments.map((appt: any) => {
              const patient = patientsMap.get(appt.patient_id) || {};
              const salutation = patient.salutation && patient.salutation !== 'null' ? `${patient.salutation} ` : '';
              return {
                appointment_id: appt.appointment_id,
                patient_id: appt.patient_id,
                name: `${salutation}${patient.full_name || 'Patient'}`.trim(),
                patient_number: patient.patient_number,
                time: appt.time,
                status: appt.status,
                checked_in_at: appt.checked_in_at,
                column: this.resolveColumn(appt.status),
              };
            });

            this.loading = false;
            this.lastRefreshedAt = new Date();
          },
          error: () => {
            this.loading = false;
          },
        });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  cardsForColumn(column: QueueBoardColumn): QueueBoardCard[] {
    return this.cards.filter((card) => card.column === column);
  }

  columnCount(column: QueueBoardColumn): number {
    return this.cardsForColumn(column).length;
  }

  formatTime(value: string): string {
    if (!value) {
      return '—';
    }
    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return value;
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  formatWaiting(card: QueueBoardCard): string {
    void this.waitingTimeTick;
    if (!card.checked_in_at || card.column === 'done') {
      return '';
    }
    const start = new Date(card.checked_in_at).getTime();
    if (!Number.isFinite(start)) {
      return '';
    }
    const totalMinutes = Math.floor(Math.max(0, Date.now() - start) / 60000);
    if (totalMinutes < 1) {
      return '<1 min';
    }
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  private resolveColumn(status: string): QueueBoardColumn {
    switch ((status || '').toLowerCase()) {
      case 'in progress':
        return 'with_doctor';
      case 'paused':
        return 'paused';
      case 'completed':
      case 'medicine':
        return 'done';
      default:
        return 'waiting';
    }
  }
}
